function formatBookingDate(iso) {
  const parts = iso.split("-");
  if (parts.length !== 3) {
    return "";
  }
  return `${parts[1]}/${parts[2]}/${parts[0]}`;
}

function todayIso() {
  const date = new Date();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

// Local midnight avoids a UTC shift turning the date into the previous day.
function isoDatePlusDay(iso) {
  const date = new Date(`${iso}T00:00:00`);
  date.setDate(date.getDate() + 1);
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

// Accepts m/d/yyyy or mm/dd/yyyy and rejects dates that are not on the calendar.
function parseBookingDate(value) {
  const match = value.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!match) {
    return "";
  }
  const month = match[1].padStart(2, "0");
  const day = match[2].padStart(2, "0");
  const year = Number(match[3]);
  const iso = `${match[3]}-${month}-${day}`;
  const date = new Date(`${iso}T00:00:00`);
  if (
    date.getFullYear() !== year ||
    date.getMonth() + 1 !== Number(month) ||
    date.getDate() !== Number(day)
  ) {
    return "";
  }
  return iso;
}

function syncDateText(nativeInput) {
  const text = nativeInput.parentElement.querySelector(".hotel-booking-date-text");
  if (!text) {
    return;
  }
  text.value = nativeInput.value ? formatBookingDate(nativeInput.value) : "";
}

// Writes a typed mm/dd/yyyy value onto the hidden date input. Returns "" when it is not a real date.
function commitDateText(text, nativeInput) {
  const typed = text.value.trim();
  if (!typed) {
    nativeInput.value = "";
    return "";
  }
  const iso = parseBookingDate(typed);
  if (!iso) {
    return "";
  }
  nativeInput.value = iso;
  text.value = formatBookingDate(iso);
  return iso;
}

// The visible field is text. The date input over the icon opens the browser calendar.
function bindDateField(nativeInput) {
  const field = nativeInput.parentElement;
  const text = field.querySelector(".hotel-booking-date-text");

  nativeInput.addEventListener("change", () => {
    syncDateText(nativeInput);
  });

  nativeInput.addEventListener("click", (event) => {
    if (typeof nativeInput.showPicker !== "function") {
      return;
    }
    const ua = navigator.userAgent;
    // Safari opens from the click itself. showPicker() there leaves a calendar that cannot close.
    const isSafari = /safari/i.test(ua) && !/chrome|chromium|crios|android/i.test(ua);
    if (isSafari) {
      return;
    }
    // Firefox's own click closes the calendar that showPicker() just opened.
    if (/firefox/i.test(ua)) {
      event.preventDefault();
    }
    try {
      nativeInput.showPicker();
    } catch {
      // The picker is already open.
    }
  });

  text.addEventListener("change", () => {
    const typed = text.value.trim();
    const iso = commitDateText(text, nativeInput);
    if (!typed || iso) {
      nativeInput.dispatchEvent(new Event("change"));
    }
  });
}

// Clear first so a repeated message is announced again.
function showBookingError(error, message) {
  error.textContent = "";
  error.textContent = message;
}

function setDateInvalid(input, error, invalid) {
  if (invalid) {
    input.setAttribute("aria-invalid", "true");
    input.setAttribute("aria-describedby", error.id);
  } else {
    input.removeAttribute("aria-invalid");
    input.removeAttribute("aria-describedby");
  }
}

function initHotelBooking(wrap) {
  const form = wrap.querySelector(".hotel-booking");
  if (!form) {
    return;
  }

  const checkin = form.querySelector("[data-hotel-checkin]");
  const checkout = form.querySelector("[data-hotel-checkout]");
  const checkinText = form.querySelector("[data-hotel-checkin-text]");
  const checkoutText = form.querySelector("[data-hotel-checkout-text]");
  const checkinField = form.querySelector('[name="Checkin"]');
  const checkoutField = form.querySelector('[name="Checkout"]');
  const subsetField = form.querySelector('[name="PromoSubset"]');
  const error = form.querySelector(".hotel-booking-error");
  const dialog = wrap.querySelector(".hotel-booking-dialog");
  const subsetCount = Number(form.getAttribute("data-subset-count")) || 0;
  const submitButton = form.querySelector('[type="submit"]');
  const today = todayIso();

  if (!checkin.min || checkin.min < today) {
    checkin.min = today;
  }
  if (checkin.value && checkin.value < today) {
    checkin.value = "";
  }

  bindDateField(checkin);
  bindDateField(checkout);
  syncDateText(checkin);
  syncDateText(checkout);

  checkin.addEventListener("change", () => {
    if (!checkin.value) {
      return;
    }
    const min = isoDatePlusDay(checkin.value);
    checkout.min = min;
    if (checkout.value && checkout.value < min) {
      checkout.value = "";
      syncDateText(checkout);
    }
  });

  form.addEventListener("submit", (event) => {
    const start = commitDateText(checkinText, checkin);
    const end = commitDateText(checkoutText, checkout);
    setDateInvalid(checkinText, error, false);
    setDateInvalid(checkoutText, error, false);

    if (!checkinText.value.trim()) {
      event.preventDefault();
      showBookingError(error, "Select a check-in date.");
      setDateInvalid(checkinText, error, true);
      checkinText.focus();
      return;
    }

    if (!start) {
      event.preventDefault();
      showBookingError(error, "Enter a check-in date as mm/dd/yyyy.");
      setDateInvalid(checkinText, error, true);
      checkinText.focus();
      return;
    }

    if (start < today) {
      event.preventDefault();
      showBookingError(error, "Check-in cannot be before today.");
      setDateInvalid(checkinText, error, true);
      checkinText.focus();
      return;
    }

    if (!checkoutText.value.trim()) {
      event.preventDefault();
      showBookingError(error, "Select a check-out date.");
      setDateInvalid(checkoutText, error, true);
      checkoutText.focus();
      return;
    }

    if (!end) {
      event.preventDefault();
      showBookingError(error, "Enter a check-out date as mm/dd/yyyy.");
      setDateInvalid(checkoutText, error, true);
      checkoutText.focus();
      return;
    }

    if (end <= start) {
      event.preventDefault();
      showBookingError(error, "Check-out must be after check-in.");
      setDateInvalid(checkoutText, error, true);
      checkoutText.focus();
      return;
    }

    showBookingError(error, "");
    checkinField.value = formatBookingDate(start);
    checkoutField.value = formatBookingDate(end);

    if (subsetCount > 1 && dialog) {
      event.preventDefault();
      if (typeof dialog.showModal === "function") {
        dialog.showModal();
      }
    }
  });

  if (!dialog) {
    return;
  }

  const subsetError = dialog.querySelector("[data-subset-error]");
  const nextButton = dialog.querySelector("[data-hotel-booking-next]");
  const closeButton = dialog.querySelector("[data-hotel-booking-close]");

  dialog.addEventListener("close", () => {
    if (submitButton) {
      submitButton.focus();
    }
  });

  closeButton.addEventListener("click", () => {
    dialog.close();
  });

  nextButton.addEventListener("click", () => {
    const selected = dialog.querySelectorAll(".subset:checked");
    if (selected.length !== 1) {
      showBookingError(
        subsetError,
        selected.length === 0
          ? "Select a program."
          : "Select only one program."
      );
      return;
    }

    showBookingError(subsetError, "");
    subsetField.value = selected[0].value;
    dialog.close();
    // Programmatic submit does not run the submit listener again.
    form.submit();
  });
}

document.addEventListener("DOMContentLoaded", () => {
  document.querySelectorAll(".hotel-booking-wrap").forEach(initHotelBooking);
});
